/**
 * Component that inserts a transparent layer with uploading abilities
 * inside another component.
 *
 * Note: The component is transparent as it's expected to be displayed
 * on top of any UI to unleash its uploading power: make sure that it's
 * the last child of that parent, or play with z-index, in order for it
 * to register clicks. The parent should be positioned.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import preventXss from 'xss';
import { stringTokenizer } from '~/i18n';
import styled from 'styled-components';

import {
  getFileTypeFromPath,
  getHumanReadableSize,
} from '~/composer/composer/utils/StringUtils';
import styles from '~/composer/composer/components/css/MediaAttachment.css';

import { Services } from '~/composer/composer/AppConstants';
import { asyncFilter } from '~/utils/asyncFilter';
import ComposerStore from '~/composer/composer/stores/ComposerStore';
import { hasMixedMediaTypes } from '~/uploads/lib/validation/validationUtils/hasMixedMediaTypes';
import { getMixedMediaError } from '~/uploads/lib/validation/validationUtils/getMixedMediaError';
import UploadMessage from '../UploadMessage';

const DropzoneWithStyles = styled(Dropzone)`
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: none;

  :focus {
    outline: none;
  }
`;

const defaultClassNames = {
  uploadZone: styles.uploadZone,
  uploadZoneActive: styles.uploadButtonUIActiveDrop,
  uploadZoneDisabled: styles.uploadButtonUIDisabled,
};

class UploadZone extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isHovering: false,
    };
  }

  setIsHovering = (isHovering) => {
    this.setState({ isHovering });
  };

  onDrop = (files, rejected, event) => {
    /*
     * Stop event propagation.
     * If we dont' do this, file drops may propogate up from Dropzone to `react-dnd`
     * and throw errors in the console; https://github.com/react-dnd/react-dnd/issues/457
     *
     * This was happening with the story groups composer when dropping a file on
     * one of the cards. (Though the error wasn't crashing the app; it just added
     * noise to our Bugsnag reporting.)
     */
    event.stopPropagation();

    const { disabled } = this.props;
    if (disabled) return;

    this.cleanUpNotifications();
    if (files.length === 0) return;

    this.uploadFiles(files);
  };

  getUploadableNewFiles = async (files, isReels, isStories) => {
    const { uploadFormatsConfig, queueError, translations, service } =
      this.props;

    let invalidFormatFilesCount = 0;

    const validFiles = await asyncFilter(files, async (file) => {
      const fileFormat = getFileTypeFromPath(file.name).toUpperCase();
      if (!uploadFormatsConfig.has(fileFormat)) {
        invalidFormatFilesCount += 1;
        return false;
      }

      const uploadFormatConfig = uploadFormatsConfig.get(fileFormat);
      const serviceConfig = Services.get(service.name);
      const isVideoAndHasServiceConfig =
        ['MOV', 'MP4', 'M4V', 'AVI', 'WEBM'].includes(
          getFileTypeFromPath(file.name).toUpperCase()
        ) && serviceConfig;

      // Video size validation - for Reels
      let isVideoAboveMaxSize = false;
      if (isVideoAndHasServiceConfig && isReels) {
        isVideoAboveMaxSize =
          serviceConfig.videoMaxSizeReels &&
          file.size > serviceConfig.videoMaxSizeReels;
      }
      // Video size validation - for Stories
      if (isVideoAndHasServiceConfig && isStories) {
        isVideoAboveMaxSize =
          serviceConfig.videoMaxSizeStories &&
          file.size > serviceConfig.videoMaxSizeStories;
      }
      // Video size validation - general
      if (isVideoAndHasServiceConfig && !isReels && !isStories) {
        isVideoAboveMaxSize =
          serviceConfig.videoMaxSize && file.size > serviceConfig.videoMaxSize;
      }

      const isImage = ['JPG', 'JPEG', 'PNG', 'WEBP'].includes(
        getFileTypeFromPath(file.name).toUpperCase()
      );

      // Image size validation for Stories
      let isImageAboveMaxSize = false;
      if (
        isStories &&
        isImage &&
        serviceConfig &&
        serviceConfig.imageMaxSizeStories &&
        file.size > serviceConfig.imageMaxSizeStories
      ) {
        isImageAboveMaxSize = true;
      }

      // Image size validation - general
      if (
        !isStories &&
        isImage &&
        serviceConfig &&
        serviceConfig.imageMaxSize &&
        file.size > serviceConfig.imageMaxSize
      ) {
        isImageAboveMaxSize = true;
      }

      // GIF size validation - general
      const isGif = getFileTypeFromPath(file.name).toUpperCase() === 'GIF';
      const isGifAboveMaxSize =
        isGif &&
        serviceConfig &&
        serviceConfig.gifMaxSize &&
        file.size > serviceConfig.gifMaxSize;

      if (isVideoAboveMaxSize || isImageAboveMaxSize || isGifAboveMaxSize) {
        const videoOrImageMaxSize = getHumanReadableSize(
          isVideoAboveMaxSize
            ? serviceConfig.videoMaxSize
            : serviceConfig.imageMaxSize
        );
        let formattedMaxSize = isGifAboveMaxSize
          ? serviceConfig.gifMaxSize
          : videoOrImageMaxSize;
        if (isStories) {
          formattedMaxSize = getHumanReadableSize(
            isVideoAboveMaxSize
              ? serviceConfig.videoMaxSizeStories
              : serviceConfig.imageMaxSizeStories
          );
        }
        if (isReels) {
          formattedMaxSize = getHumanReadableSize(
            serviceConfig.videoMaxSizeReels
          );
        }

        let message = translations.invalidFileSize;
        message = stringTokenizer(message, '{fileName}', preventXss(file.name));
        message = stringTokenizer(
          message,
          '{formattedMaxSize}',
          formattedMaxSize
        );

        if (
          isVideoAboveMaxSize &&
          service.name === 'instagram' &&
          !isReels &&
          !isStories
        ) {
          message = `${message.substr(
            0,
            message.length - 1
          )} or select Reel in the composer?`;
        }

        queueError({ message });
        return false;
      }

      if (file.size > uploadFormatConfig.maxSize) {
        const formattedMaxSize = getHumanReadableSize(
          uploadFormatConfig.maxSize
        );
        let message = translations.invalidFileSize;
        message = stringTokenizer(message, '{fileName}', preventXss(file.name));
        message = stringTokenizer(
          message,
          '{formattedMaxSize}',
          formattedMaxSize
        );

        queueError({ message });
        return false;
      }

      if (service.attachmentMinSize && file.size < service.attachmentMinSize) {
        const formattedMinSize = getHumanReadableSize(
          service.attachmentMinSize
        );

        let message = translations.tooSmallFileSize;
        message = stringTokenizer(message, '{fileName}', preventXss(file.name));
        message = stringTokenizer(
          message,
          '{formattedMinSize}',
          formattedMinSize
        );

        queueError({ message });
        return false;
      }

      return true;
    });

    if (invalidFormatFilesCount > 0) {
      const acceptedFilesText = [...uploadFormatsConfig.keys()].join(', ');
      let message;

      if (invalidFormatFilesCount > 1) {
        if (invalidFormatFilesCount === files.length) {
          message = translations.invalidFormatCantUseAnyFiles;
        } else {
          message = translations.invalidFormatCantUseSomeFiles;
        }
      } else if (invalidFormatFilesCount === 1) {
        if (files.length > 1) {
          message = translations.invalidFormatCantOneOfFiles;
        } else {
          message = translations.invalidFormatCantSingleFile;
        }
      }

      // we want to insert our tokenized accepedFilesText string into our message where appropriate
      message = stringTokenizer(
        message,
        '{acceptedFilesText}',
        acceptedFilesText
      );

      queueError({ message });
    }

    return validFiles;
  };

  uploadFiles = async (files) => {
    const {
      draftId,
      service,
      uploadDraftFile,
      queueError,
      uploadType,
      notifiers,
    } = this.props;
    const { maxAttachableImagesCount } = service;

    if (hasMixedMediaTypes(files)) {
      const message = getMixedMediaError(service);
      queueError({ message });
      return;
    }

    // Truncate files to upload to the max attachable images count
    if (files.length > maxAttachableImagesCount) {
      files.splice(maxAttachableImagesCount);
    }

    const isReels = ComposerStore.getDraft(draftId)?.isReelsPost() || false;
    const isStories = ComposerStore.getDraft(draftId)?.isStoryPost() || false;

    const uploadableNewFiles = await this.getUploadableNewFiles(
      files,
      isReels,
      isStories
    );

    if (uploadableNewFiles.length > 0) {
      uploadableNewFiles.forEach((file) => {
        uploadDraftFile(draftId, file, uploadType, notifiers);
      });
    }
  };

  cleanUpNotifications = () => {
    const { removeAllNotifications } = this.props;
    removeAllNotifications();
  };

  openFileUploader = () => {
    this.dropzone.open();
  };

  render() {
    const {
      classNames,
      multiple,
      disabled,
      showUploadMessage,
      isImageFirst,
      onlyAllowsVerticalVideo,
    } = this.props;

    const { isHovering } = this.state;
    const {
      uploadZoneActive,
      uploadZoneDisabled,
      uploadZone: uploadZoneClassname,
    } = classNames || defaultClassNames;

    return (
      <div
        onClick={() => this.openFileUploader()}
        onMouseEnter={() => this.setIsHovering(true)}
        onMouseLeave={() => this.setIsHovering(false)}
      >
        <UploadMessage
          showMessage={showUploadMessage}
          isHovering={isHovering}
          isImageFirst={isImageFirst}
          onlyAllowsVerticalVideo={onlyAllowsVerticalVideo}
        />
        <DropzoneWithStyles
          onDragOver={(event) => event.stopPropagation()}
          onDrop={this.onDrop}
          activeClassName={uploadZoneActive}
          disabledClassName={uploadZoneDisabled}
          className={uploadZoneClassname}
          ref={(node) => {
            this.dropzone = node;
          }}
          multiple={multiple}
          disabled={disabled}
          disableClick
          data-testid="composer-uploader-dropzone"
        />
      </div>
    );
  }
}

UploadZone.propTypes = {
  draftId: PropTypes.string.isRequired,
  classNames: PropTypes.shape({
    uploadZone: PropTypes.string,
    uploadZoneActive: PropTypes.string,
    uploadZoneDisabled: PropTypes.string,
  }),
  uploadFormatsConfig: PropTypes.instanceOf(Map).isRequired,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  uploadType: PropTypes.string.isRequired,
  service: PropTypes.shape({
    maxAttachableImagesCount: PropTypes.number,
    canHaveMediaAttachmentType: PropTypes.func,
    name: PropTypes.string,
    attachmentMinSize: PropTypes.number,
  }).isRequired,
  uploadDraftFile: PropTypes.func.isRequired,
  removeAllNotifications: PropTypes.func.isRequired,
  queueError: PropTypes.func.isRequired,
  notifiers: PropTypes.shape({
    uploadStarted: PropTypes.func,
    uploadedLinkThumbnail: PropTypes.func,
    uploadedDraftImage: PropTypes.func,
    uploadedDraftVideo: PropTypes.func,
    draftGifUploaded: PropTypes.func,
    queueError: PropTypes.func,
  }).isRequired,
  translations: PropTypes.shape({
    invalidFormatCantUseAnyFiles: PropTypes.string,
    invalidFormatCantUseSomeFiles: PropTypes.string,
    invalidFormatCantOneOfFiles: PropTypes.string,
    invalidFormatCantSingleFile: PropTypes.string,
    invalidFileSize: PropTypes.string,
    tooSmallFileSize: PropTypes.string,
    buttonTitle: PropTypes.string,
  }).isRequired,
  showUploadMessage: PropTypes.bool,
  isImageFirst: PropTypes.bool,
  onlyAllowsVerticalVideo: PropTypes.bool,
};

UploadZone.defaultProps = {
  disabled: false,
  multiple: true,
  classNames: {
    uploadZone: '',
    uploadZoneActive: '',
    uploadZoneDisabled: '',
  },
  showUploadMessage: false,
  isImageFirst: false,
  onlyAllowsVerticalVideo: false,
};

export default UploadZone;
